import * as React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { cn } from "../lib/utils";
import { LEAD_STATUS_META, type LeadStatus } from "../lead-status";

export type TimeseriesBucket = "day" | "week" | "month";

export interface TimeseriesPoint {
  bucket: string; // "YYYY-MM-DD"
  counts: Record<string, number>;
}

export interface StatusTimeseriesChartProps {
  data: TimeseriesPoint[];
  bucket: TimeseriesBucket;
  onBucketChange: (b: TimeseriesBucket) => void;
  loading?: boolean;
  // 지정 시 이 상태들만 렌더 (상태 필터). 미지정 = 데이터 있는 전부.
  visibleStatuses?: LeadStatus[];
}

function fmtBucket(b: string, bucket: TimeseriesBucket): string {
  if (bucket === "month") return b.slice(0, 7); // YYYY-MM
  return b.slice(5); // MM-DD
}

const BUCKET_LABEL: Record<TimeseriesBucket, string> = {
  day: "일간",
  week: "주간",
  month: "월간",
};

// 막대 hover 툴팁 — 상태별 개수(전체 대비 %) + 맨 아래 총계.
function ChartTooltip(props: {
  active?: boolean;
  label?: string | number;
  payload?: Array<{
    name?: string;
    value?: number | string;
    color?: string;
    dataKey?: string | number;
  }>;
}): React.ReactElement | null {
  const { active, payload, label } = props;
  if (!active || !payload || payload.length === 0) return null;
  const total = payload.reduce((s, p) => s + (Number(p.value) || 0), 0);
  const rows = payload.filter((p) => (Number(p.value) || 0) > 0);
  return (
    <div className="rounded-md border border-border bg-surface px-3 py-2 shadow-md text-[12px] min-w-[140px]">
      <div className="font-semibold mb-1 text-ink">{label}</div>
      {rows.map((p) => {
        const v = Number(p.value) || 0;
        const pct = total > 0 ? ((v / total) * 100).toFixed(1) : "0.0";
        return (
          <div
            key={String(p.dataKey)}
            className="flex items-center justify-between gap-3"
          >
            <span className="flex items-center gap-1.5">
              <span
                className="w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: p.color }}
              />
              <span className="text-ink-muted">{p.name}</span>
            </span>
            <span className="tabular-nums text-ink">
              {v} ({pct}%)
            </span>
          </div>
        );
      })}
      <div className="flex items-center justify-between gap-3 mt-1 pt-1 border-t border-border font-semibold">
        <span className="text-ink">총계</span>
        <span className="tabular-nums text-ink">{total}</span>
      </div>
    </div>
  );
}

export function StatusTimeseriesChart({
  data,
  bucket,
  onBucketChange,
  loading,
  visibleStatuses,
}: StatusTimeseriesChartProps): React.ReactElement {
  // 데이터에 등장하는 상태만 렌더 (레전드 정리) + 상태 필터 교집합. 메타 순서 유지.
  const present = React.useMemo(() => {
    const totals = new Map<string, number>();
    for (const p of data)
      for (const [k, v] of Object.entries(p.counts))
        totals.set(k, (totals.get(k) ?? 0) + v);
    const allow = visibleStatuses ? new Set(visibleStatuses) : null;
    return LEAD_STATUS_META.filter(
      (m) =>
        (totals.get(m.value) ?? 0) > 0 && (!allow || allow.has(m.value)),
    );
  }, [data, visibleStatuses]);

  const rows = React.useMemo(
    () =>
      data.map((p) => {
        const row: Record<string, string | number> = {
          bucket: fmtBucket(p.bucket, bucket),
        };
        for (const m of present) row[m.value] = p.counts[m.value] ?? 0;
        return row;
      }),
    [data, present, bucket],
  );

  return (
    <div>
      <div className="flex items-center gap-1 mb-2">
        {(["day", "week", "month"] as TimeseriesBucket[]).map((b) => (
          <button
            key={b}
            type="button"
            onClick={() => onBucketChange(b)}
            className={cn(
              "px-2.5 py-1 text-[12px] rounded-base border transition-colors",
              b === bucket
                ? "bg-primary text-white border-primary"
                : "border-border text-ink-muted hover:bg-surface-muted",
            )}
          >
            {BUCKET_LABEL[b]}
          </button>
        ))}
      </div>
      <div style={{ width: "100%", height: 260 }}>
        {loading ? (
          <div className="h-full flex items-center justify-center text-ink-subtle text-[13px]">
            불러오는 중…
          </div>
        ) : rows.length === 0 ? (
          <div className="h-full flex items-center justify-center text-ink-subtle text-[13px]">
            해당 기간에 데이터가 없습니다.
          </div>
        ) : (
          <ResponsiveContainer>
            <BarChart data={rows} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
              <XAxis dataKey="bucket" tick={{ fontSize: 11 }} stroke="#a1a1aa" />
              <YAxis tick={{ fontSize: 11 }} stroke="#a1a1aa" allowDecimals={false} />
              <Tooltip
                cursor={{ fill: "rgb(0 0 0 / 0.04)" }}
                content={<ChartTooltip />}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {present.map((m) => (
                <Bar
                  key={m.value}
                  dataKey={m.value}
                  name={m.label}
                  stackId="s"
                  fill={m.color}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
